import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  FormHelperText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES,
  EXPANDED_HOTEL_YEAR_ROUND_INPUT_CODES,
  operationalMonthsSeparator,
} from 'common/constants';
import type { FormikProps } from 'formik';

import { type FC, useEffect, useMemo } from 'react';
import { MultiSelectChipsChoices } from '../lib/MultiSelectChipsChoices';
import type { Field } from '../lib/QuestionEngine';
import { RenderFieldWithWrapper } from '../lib/QuestionEngine/QuestionEngine.components';
import type { QuestionEngineStateValues } from '../lib/QuestionEngine/QuestionEngine.types';
import { ToggleButtonsGroup } from '../lib/ToggleButtonsGroup';
import { computePayrollWorksheetTotals } from './helpers/computePayrollWorksheetTotals';

export const PAYROLL_WORKSHEET_FIELD_CODES = {
  isSeasonalBusiness: 'isSeasonalBusiness',
  operationalMonths: 'operationalMonths',
  fullTimeEmployeeCount: 'fullTimeEmployeeCount',
  fullTimeAvgHourlyWage: 'fullTimeAvgHourlyWage',
  fullTimeAvgWeeklyHours: 'fullTimeAvgWeeklyHours',
  fullTimeEstimatedAnnualPayroll: 'fullTimeEstimatedAnnualPayroll',
  partTimeEmployeeCount: 'partTimeEmployeeCount',
  partTimeAvgHourlyWage: 'partTimeAvgHourlyWage',
  partTimeAvgWeeklyHours: 'partTimeAvgWeeklyHours',
  partTimeEstimatedAnnualPayroll: 'partTimeEstimatedAnnualPayroll',
} as const;

const EXPANDED_HOTEL_FIELD_CODE_SET = new Set<string>(
  Object.values(EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES),
);

const YEAR_ROUND_ESTIMATED_PAYROLL_CODES: string[] = [
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtEstimatedAnnualPayroll,
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtEstimatedAnnualPayroll,
];

const getFieldsByCodes = (fields: Field[], codes: string[]): Field[] =>
  codes
    .map((code) => fields.find((field) => field?.code === code))
    .filter((field): field is Field => Boolean(field));

const IS_SEASONAL_BUSINESS_FIELD: Field = {
  code: 'isSeasonalBusiness',
  is_optional: false,
  is_hidden: false,
  is_readonly: false,
  question_text: 'Is this a seasonal business?',
  show_condition: {
    reference: 'payrollWorksheet',
    relational_operator: '==',
    value: 'Yes',
    value_type: 'string',
  },
  newline: false,
  type: 'select_button',
  choices: [
    { code: 'Yes', name: 'Yes' },
    { code: 'No', name: 'No' },
  ],
};

interface PayrollWorksheetContainerProps {
  state: Record<string, unknown>;
  setState: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  formik: FormikProps<QuestionEngineStateValues>;
  payrollWorksheetFields?: Field[];
  isReadOnly?: boolean;
  readonlyFieldCodes?: string[];
  isExpandedHotel?: boolean;
}

const PayrollWorksheetContainer: FC<PayrollWorksheetContainerProps> = ({
  state,
  setState,
  formik,
  payrollWorksheetFields = [],
  isReadOnly = false,
  readonlyFieldCodes = [],
  isExpandedHotel = false,
  // eslint-disable-next-line complexity
}) => {
  const theme = useTheme();

  const effectiveWorksheetFields = useMemo((): Field[] => {
    if (isExpandedHotel) {
      return payrollWorksheetFields;
    }
    return payrollWorksheetFields.filter(
      (field) => field?.code && !EXPANDED_HOTEL_FIELD_CODE_SET.has(field.code),
    );
  }, [payrollWorksheetFields, isExpandedHotel]);

  const mergedReadonlyFieldCodes = useMemo((): string[] => {
    if (!isExpandedHotel) {
      return readonlyFieldCodes;
    }
    return [...readonlyFieldCodes, ...YEAR_ROUND_ESTIMATED_PAYROLL_CODES];
  }, [isExpandedHotel, readonlyFieldCodes]);

  const isSeasonal = state?.[PAYROLL_WORKSHEET_FIELD_CODES.isSeasonalBusiness] === 'Yes';
  const isSeasonalNoSelected = state?.[PAYROLL_WORKSHEET_FIELD_CODES.isSeasonalBusiness] === 'No';
  const operationalMonthsValue = (state?.[PAYROLL_WORKSHEET_FIELD_CODES.operationalMonths] ??
    '') as string;
  const isYearRoundEmployees =
    state?.[EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.hasYearRoundEmployees] === 'Yes';

  const {
    fullTimeEstimatedAnnualPayroll,
    partTimeEstimatedAnnualPayroll,
    yearRoundFtEstimatedAnnualPayroll,
    yearRoundPtEstimatedAnnualPayroll,
  } = computePayrollWorksheetTotals(state, isExpandedHotel);

  useEffect(() => {
    if (!isReadOnly && !isSeasonal && operationalMonthsValue) {
      setState((prev: Record<string, unknown>) => ({
        ...prev,
        [PAYROLL_WORKSHEET_FIELD_CODES.operationalMonths]: '',
      }));
      formik.setFieldValue(PAYROLL_WORKSHEET_FIELD_CODES.operationalMonths, '');
    }
  }, [isSeasonal, isReadOnly]);

  useEffect(() => {
    if (!isReadOnly) {
      const value = String(fullTimeEstimatedAnnualPayroll);
      setState((prev: Record<string, unknown>) => ({
        ...prev,
        [PAYROLL_WORKSHEET_FIELD_CODES.fullTimeEstimatedAnnualPayroll]: value,
      }));
      formik.setFieldValue(PAYROLL_WORKSHEET_FIELD_CODES.fullTimeEstimatedAnnualPayroll, value);
    }
  }, [fullTimeEstimatedAnnualPayroll, isReadOnly]);

  useEffect(() => {
    if (!isReadOnly) {
      const value = String(partTimeEstimatedAnnualPayroll);
      setState((prev: Record<string, unknown>) => ({
        ...prev,
        [PAYROLL_WORKSHEET_FIELD_CODES.partTimeEstimatedAnnualPayroll]: value,
      }));
      formik.setFieldValue(PAYROLL_WORKSHEET_FIELD_CODES.partTimeEstimatedAnnualPayroll, value);
    }
  }, [partTimeEstimatedAnnualPayroll, isReadOnly]);

  useEffect(() => {
    if (isReadOnly || !isExpandedHotel || isYearRoundEmployees) {
      return;
    }
    const resetPayload: Record<string, string> = {};
    EXPANDED_HOTEL_YEAR_ROUND_INPUT_CODES.forEach((code) => {
      resetPayload[code] = '';
    });
    setState((prev: Record<string, unknown>) => ({ ...prev, ...resetPayload }));
    EXPANDED_HOTEL_YEAR_ROUND_INPUT_CODES.forEach((code) => formik.setFieldValue(code, ''));
  }, [isExpandedHotel, isReadOnly, isYearRoundEmployees]);

  useEffect(() => {
    if (isReadOnly || !isExpandedHotel || isSeasonal) {
      return;
    }
    const key = EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.hasYearRoundEmployees;
    const currentValue = state?.[key];
    if (!currentValue || String(currentValue).trim() === '') {
      return;
    }
    const resetPayload: Record<string, string> = { [key]: '' };
    EXPANDED_HOTEL_YEAR_ROUND_INPUT_CODES.forEach((code) => {
      resetPayload[code] = '';
    });
    setState((prev: Record<string, unknown>) => ({ ...prev, ...resetPayload }));
    Object.keys(resetPayload).forEach((code) => formik.setFieldValue(code, ''));
  }, [isExpandedHotel, isReadOnly, isSeasonal]);

  useEffect(() => {
    if (isReadOnly || !isExpandedHotel || !isYearRoundEmployees) {
      return;
    }
    const fullTimeValue = String(yearRoundFtEstimatedAnnualPayroll);
    const partTimeValue = String(yearRoundPtEstimatedAnnualPayroll);
    setState((prev: Record<string, unknown>) => ({
      ...prev,
      [EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtEstimatedAnnualPayroll]:
        fullTimeValue,
      [EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtEstimatedAnnualPayroll]:
        partTimeValue,
    }));
    formik.setFieldValue(
      EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtEstimatedAnnualPayroll,
      fullTimeValue,
    );
    formik.setFieldValue(
      EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtEstimatedAnnualPayroll,
      partTimeValue,
    );
  }, [
    isExpandedHotel,
    isReadOnly,
    isYearRoundEmployees,
    yearRoundFtEstimatedAnnualPayroll,
    yearRoundPtEstimatedAnnualPayroll,
  ]);

  const hasYearRoundEmployeesField = useMemo(
    () =>
      effectiveWorksheetFields.find(
        (field) =>
          field?.code === EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.hasYearRoundEmployees,
      ),
    [effectiveWorksheetFields],
  );

  useEffect(() => {
    if (
      isReadOnly ||
      !isExpandedHotel ||
      !isSeasonal ||
      state?.payrollWorksheet !== 'Yes' ||
      !hasYearRoundEmployeesField
    ) {
      return;
    }
    const key = EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.hasYearRoundEmployees;
    const current = state?.[key];
    const isUnset = current === undefined || current === null || String(current).trim() === '';
    if (!isUnset) {
      return;
    }
    setState((prev: Record<string, unknown>) => ({ ...prev, [key]: 'No' }));
    formik.setFieldValue(key, 'No');
  }, [
    isExpandedHotel,
    isSeasonal,
    isReadOnly,
    state?.payrollWorksheet,
    state?.[EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.hasYearRoundEmployees],
    hasYearRoundEmployeesField,
  ]);

  const operationalMonthsField = useMemo(
    () =>
      effectiveWorksheetFields.find(
        (field) => field?.code === PAYROLL_WORKSHEET_FIELD_CODES.operationalMonths,
      ),
    [effectiveWorksheetFields],
  );

  const fullTimeFields = useMemo(
    () =>
      getFieldsByCodes(effectiveWorksheetFields, [
        'fullTimeEmployeeCount',
        'fullTimeAvgHourlyWage',
        'fullTimeAvgWeeklyHours',
        'fullTimeEstimatedAnnualPayroll',
      ]),
    [effectiveWorksheetFields],
  );

  const partTimeFields = useMemo(
    () =>
      getFieldsByCodes(effectiveWorksheetFields, [
        'partTimeEmployeeCount',
        'partTimeAvgHourlyWage',
        'partTimeAvgWeeklyHours',
        'partTimeEstimatedAnnualPayroll',
      ]),
    [effectiveWorksheetFields],
  );

  const yearRoundFullTimeFields = useMemo(
    () =>
      getFieldsByCodes(effectiveWorksheetFields, [
        EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtEmployeeCount,
        EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtAvgHourlyWage,
        EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtAvgWeeklyHours,
        EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtEstimatedAnnualPayroll,
      ]),
    [effectiveWorksheetFields],
  );

  const yearRoundPartTimeFields = useMemo(
    () =>
      getFieldsByCodes(effectiveWorksheetFields, [
        EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtEmployeeCount,
        EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtAvgHourlyWage,
        EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtAvgWeeklyHours,
        EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtEstimatedAnnualPayroll,
      ]),
    [effectiveWorksheetFields],
  );

  if (payrollWorksheetFields.length === 0) {
    return null;
  }

  const renderFieldsInGrid = (fields: Field[]): React.ReactNode => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: '1fr 1fr',
        },
        gap: 2,
        width: '100%',
      }}
    >
      {fields.map((field, index) => (
        <Box
          key={field.code}
          sx={{
            minWidth: 0,
            '& .MuiFormControl-root, & .MuiTextField-root, & .MuiInputBase-root': {
              width: '100%',
            },
          }}
        >
          <RenderFieldWithWrapper
            field={field}
            formik={formik}
            state={state}
            setState={setState}
            isReadOnly={isReadOnly || mergedReadonlyFieldCodes.includes(field.code)}
            idx={index}
            sx={{ width: '100%' }}
          />
        </Box>
      ))}
    </Box>
  );

  const seasonalQuestionBlock = (
    <>
      <Stack gap={1}>
        <Typography variant="h3" sx={{ mb: 0 }}>
          {IS_SEASONAL_BUSINESS_FIELD.question_text} *
        </Typography>
        <FormControl
          error={
            (formik.touched as Record<string, boolean>)?.[IS_SEASONAL_BUSINESS_FIELD.code] &&
            Boolean((formik.errors as Record<string, string>)?.[IS_SEASONAL_BUSINESS_FIELD.code])
          }
        >
          <ToggleButtonsGroup
            id={IS_SEASONAL_BUSINESS_FIELD.code}
            value={(state?.[IS_SEASONAL_BUSINESS_FIELD.code] ?? '') as string}
            onChange={(_, value: string) => {
              if (value == null) return;
              setState((prev) => ({
                ...prev,
                [IS_SEASONAL_BUSINESS_FIELD.code]: value,
              }));
              formik.setFieldValue(IS_SEASONAL_BUSINESS_FIELD.code, value);
            }}
            options={
              IS_SEASONAL_BUSINESS_FIELD.choices?.map((c) => ({
                value: c.code,
                label: c.name,
              })) ?? []
            }
            testPrefix={`q_${IS_SEASONAL_BUSINESS_FIELD.code}`}
            isReadOnly={isReadOnly}
            sx={{
              gridTemplateColumns: '140px 140px',
              gap: '16px',
              width: 'fit-content',
              '& .MuiToggleButton-root': {
                borderRadius: '.25rem',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                '& > div:first-of-type': {
                  justifyContent: 'center',
                  width: 'auto',
                },
              },
            }}
          />
          {(formik.touched as Record<string, boolean>)?.[IS_SEASONAL_BUSINESS_FIELD.code] &&
            (formik.errors as Record<string, string>)?.[IS_SEASONAL_BUSINESS_FIELD.code] && (
              <FormHelperText>
                {(formik.errors as Record<string, string>)?.[IS_SEASONAL_BUSINESS_FIELD.code]}
              </FormHelperText>
            )}
        </FormControl>
      </Stack>

      {isSeasonal && operationalMonthsField && (
        <MultiSelectChipsChoices
          field={operationalMonthsField}
          formik={formik}
          state={state}
          setState={setState}
          isReadOnly={isReadOnly}
          separator={operationalMonthsSeparator}
        />
      )}
    </>
  );

  const hasYearRoundToggle = hasYearRoundEmployeesField ? (
    <Stack gap={1}>
      <Typography variant="h3" sx={{ mb: 0 }}>
        {hasYearRoundEmployeesField.question_text ?? 'Do you have employees who work year-round?'} *
      </Typography>
      <FormControl
        error={
          (formik.touched as Record<string, boolean>)?.[
            EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.hasYearRoundEmployees
          ] &&
          Boolean(
            (formik.errors as Record<string, string>)?.[
              EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.hasYearRoundEmployees
            ],
          )
        }
      >
        <ToggleButtonsGroup
          id={EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.hasYearRoundEmployees}
          value={
            (state?.[EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.hasYearRoundEmployees] ??
              '') as string
          }
          onChange={(_, value: string) => {
            if (value == null) return;
            setState((prev) => ({
              ...prev,
              [EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.hasYearRoundEmployees]: value,
            }));
            formik.setFieldValue(
              EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.hasYearRoundEmployees,
              value,
            );
          }}
          options={[
            { value: 'Yes', label: 'Yes' },
            { value: 'No', label: 'No' },
          ]}
          testPrefix={`q_${EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.hasYearRoundEmployees}`}
          isReadOnly={isReadOnly}
          sx={{
            gridTemplateColumns: '140px 140px',
            gap: '16px',
            width: 'fit-content',
            '& .MuiToggleButton-root': {
              borderRadius: '.25rem',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              '& > div:first-of-type': {
                justifyContent: 'center',
                width: 'auto',
              },
            },
          }}
        />
        {(formik.touched as Record<string, boolean>)?.[
          EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.hasYearRoundEmployees
        ] &&
          (formik.errors as Record<string, string>)?.[
            EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.hasYearRoundEmployees
          ] && (
            <FormHelperText>
              {
                (formik.errors as Record<string, string>)?.[
                  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.hasYearRoundEmployees
                ]
              }
            </FormHelperText>
          )}
      </FormControl>
    </Stack>
  ) : null;

  return (
    <Accordion
      data-test="payroll-worksheet-container"
      sx={{
        width: '100%',
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '& > .MuiCollapse-root': {
          backgroundColor: theme.palette.customColors.white50,
          borderBottomRightRadius: 0.5,
          borderBottomLeftRadius: 0.5,
          border: (theme) => `0.5px solid ${theme.palette.customColors.payrollWorksheetBorder}`,
        },
      }}
      defaultExpanded
    >
      <AccordionSummary
        expandIcon={<ExpandMore sx={{ fontSize: 20, color: theme.palette.customColors.white50 }} />}
        sx={{
          minHeight: 'unset',
          borderRadius: 1,
          '&.Mui-expanded': {
            minHeight: 'unset',
            mt: '0px !important',
            borderBottomRightRadius: '0px !important',
            borderBottomLeftRadius: '0px !important',
            boxShadow: (theme) =>
              `inset 0px -1px 0px ${theme.palette.customColors.payrollWorksheetDivider}`,
          },
          '& > .MuiAccordionSummary-content': {
            my: 0,
            px: 0,
            py: 1,
            alignItems: 'center',
          },
          '& > .MuiAccordionSummary-content.Mui-expanded': {
            my: '0px !important',
          },
          backgroundColor: theme.palette.customColors.payrollWorksheetHeader,
          py: 1,
          alignItems: 'flex-start',
          '& .MuiAccordionSummary-expandIconWrapper': {
            mt: 0.25,
          },
        }}
      >
        <Box>
          <Typography variant="h2" color={theme.palette.customColors.white50}>
            Please enter some additional details.
          </Typography>
          <Typography variant="body2" color={theme.palette.customColors.white50}>
            Number of employees and the total annual wage information will be updated based on
            amounts entered here.
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          backgroundColor: theme.palette.customColors.white50,
          borderBottomRightRadius: 1,
          borderBottomLeftRadius: 1,
          p: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {isExpandedHotel ? (
            <>
              {seasonalQuestionBlock}

              {isSeasonalNoSelected && (
                <>
                  {fullTimeFields.length > 0 && (
                    <Box>
                      <Typography variant="h2">For Full Time Employees</Typography>
                      {renderFieldsInGrid(fullTimeFields)}
                    </Box>
                  )}

                  {partTimeFields.length > 0 && (
                    <Box>
                      <Typography variant="h2">For Part Time Employees</Typography>
                      {renderFieldsInGrid(partTimeFields)}
                    </Box>
                  )}
                </>
              )}

              {isSeasonal && (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="h2">Seasonal employees</Typography>
                    <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
                      Full Time
                    </Typography>
                    {renderFieldsInGrid(fullTimeFields)}
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      Part Time
                    </Typography>
                    {renderFieldsInGrid(partTimeFields)}
                  </Box>

                  {hasYearRoundToggle}

                  {isYearRoundEmployees &&
                    (yearRoundFullTimeFields.length > 0 || yearRoundPartTimeFields.length > 0) && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                          <Typography variant="h2">Year-Round Employees</Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Employed for all year regardless of season.
                          </Typography>
                        </Box>
                        {yearRoundFullTimeFields.length > 0 && (
                          <>
                            <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
                              Full Time
                            </Typography>
                            {renderFieldsInGrid(yearRoundFullTimeFields)}
                          </>
                        )}
                        {yearRoundPartTimeFields.length > 0 && (
                          <>
                            <Typography variant="subtitle2" sx={{ mt: 1 }}>
                              Part Time
                            </Typography>
                            {renderFieldsInGrid(yearRoundPartTimeFields)}
                          </>
                        )}
                      </Box>
                    )}
                </>
              )}
            </>
          ) : (
            <>
              {seasonalQuestionBlock}

              {fullTimeFields.length > 0 && (
                <Box>
                  <Typography variant="h2">For Full Time Employees</Typography>
                  {renderFieldsInGrid(fullTimeFields)}
                </Box>
              )}

              {partTimeFields.length > 0 && (
                <Box>
                  <Typography variant="h2">For Part Time Employees</Typography>
                  {renderFieldsInGrid(partTimeFields)}
                </Box>
              )}
            </>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default PayrollWorksheetContainer;
